/**
 * @file 后台业务壳层
 * @description 固定品牌区与侧栏四菜单；顶栏含面包屑、主题入口与用户菜单。
 *
 * @whenToUse 登录后业务页的统一外壳（校区 / 教师 / 学员 / 讲义）。
 * @whenNotToUse 登录页、Showcase 预览页。
 */

import { useEffect, type ReactNode } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import {
  Building2,
  GraduationCap,
  Printer,
  Users,
} from 'lucide-react'
import iconUrl from '@/assets/icon.png'
import { AdminTopbarActions } from '@/components/admin-topbar-actions.tsx'
import { Breadcrumb } from '@/components/ui/breadcrumb.tsx'
import type { SidebarItem } from '@/components/ui/sidebar.tsx'
import { AdminLayout } from '@/layouts/admin-layout.tsx'
import { useUserStore } from '@/stores/user-store.ts'

/** 侧栏仅保留的四个业务菜单 */
export const adminMenuItems: SidebarItem[] = [
  {
    key: 'campuses',
    label: '校区管理',
    to: '/',
    icon: <Building2 aria-hidden />,
  },
  {
    key: 'teachers',
    label: '教师管理',
    to: '/teachers',
    icon: <GraduationCap aria-hidden />,
  },
  {
    key: 'students',
    label: '学员管理',
    to: '/students',
    icon: <Users aria-hidden />,
  },
  {
    key: 'handouts',
    label: '讲义打印',
    to: '/handouts',
    icon: <Printer aria-hidden />,
  },
]

type AdminShellProps = {
  /** 覆盖顶栏折叠按钮右侧内容；默认按路由生成面包屑 */
  topbarLeft?: ReactNode
  /** 顶栏右侧；默认剩余点数 + 主题按钮 + 用户菜单 */
  topbarRight?: ReactNode
}

/**
 * 按当前路径生成「首页 / 当前菜单」面包屑。
 * @param pathname 当前路由 pathname
 * @returns 面包屑项
 */
function buildAdminBreadcrumb(pathname: string) {
  const current = adminMenuItems.find((item) => item.to === pathname)

  return [
    { key: 'home', label: '首页', href: '/' },
    ...(current
      ? [{ key: current.key, label: current.label }]
      : []),
  ]
}

/**
 * 带侧栏四菜单的后台壳；子路由渲染在内容区。
 */
export function AdminShell({
  topbarLeft,
  topbarRight,
}: AdminShellProps) {
  const { pathname } = useLocation()
  const fetchUser = useUserStore((s) => s.fetchUser)

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  return (
    <AdminLayout
      items={adminMenuItems}
      brand={(collapsed) => (
        <Link
          to="/"
          className="flex min-w-0 items-center gap-2 font-semibold text-foreground transition-opacity duration-200 hover:opacity-90"
        >
          <img
            src={iconUrl}
            alt="思犇优学"
            className="size-8 shrink-0 rounded-md object-cover"
          />
          {!collapsed ? (
            <span className="truncate text-sm">思犇优学</span>
          ) : null}
        </Link>
      )}
      topbarLeft={
        topbarLeft ?? <Breadcrumb items={buildAdminBreadcrumb(pathname)} />
      }
      topbarRight={topbarRight ?? <AdminTopbarActions />}
    >
      <Outlet />
    </AdminLayout>
  )
}
